/**
 * Login por biometria — estratégia "Betano".
 *
 * O nativo guarda email + senha cifrados (Keystore + biometria). No unlock,
 * o nativo devolve as credenciais e o web SUBMETE o form de login normal —
 * o backend responde com Set-Cookie como em qualquer login.
 *
 * Não tentamos restaurar cookies HttpOnly via JS/nativo: provou-se que não
 * propaga para a WebView do Android em todos os cenários.
 *
 * Comportamento "Betano":
 *  - Após enroll, toda abertura do app dispara unlock automático.
 *  - Após logout manual, credencial fica em estado "locked": permanece
 *    no SecureStore (não é apagada), mas não pode ser usada até o usuário
 *    fazer um novo login manual com sucesso (re-arma a biometria).
 *  - Login mascarado (`maskedLogin`) é devolvido pelo nativo sem precisar
 *    de biometria — usado só para exibir no botão.
 */

import { useNativeBridge } from '~/composables/useNativeBridge';
import {
  BiometricAvailabilityEvent,
  BiometricDisableEvent,
  BiometricEnrollEvent,
  BiometricUnlockEvent,
} from '~/shared/NativeBridge';

interface AvailabilityResult {
  hasHardware: boolean;
  isEnrolled: boolean;
  types: string[];
  hasCredential: boolean;
  /** Credencial existe mas está bloqueada após logout manual. */
  isLocked: boolean;
  /** Login mascarado para UI (ex.: "***.***.***-12" ou "j***@gmail.com"). */
  maskedLogin: string | null;
}

interface EnrolledResult {
  success: boolean;
  error?: string;
}

interface UnlockedResult {
  success: boolean;
  login?: string;
  password?: string;
  error?: string;
}

const OPT_IN_DISMISSED_KEY = 'biometric_opt_in_dismissed';
const CREDENTIAL_OWNER_KEY = 'biometric_credential_owner';

const EMPTY_AVAILABILITY: AvailabilityResult = {
  hasHardware: false,
  isEnrolled: false,
  types: [],
  hasCredential: false,
  isLocked: false,
  maskedLogin: null,
};

export const useBiometricAuth = () => {
  const { isNativeApp, dispatch, on } = useNativeBridge();

  function checkAvailability(): Promise<AvailabilityResult> {
    return new Promise((resolve) => {
      if (!isNativeApp()) {
        return resolve(EMPTY_AVAILABILITY);
      }

      const off = on<AvailabilityResult>('biometricAvailability', (result) => {
        off();
        resolve(result);
      });

      dispatch(new BiometricAvailabilityEvent());

      setTimeout(() => {
        off();
        resolve(EMPTY_AVAILABILITY);
      }, 5000);
    });
  }

  function enroll(login: string, password: string): Promise<EnrolledResult> {
    return new Promise((resolve) => {
      if (!isNativeApp()) {
        return resolve({ success: false, error: 'not_implemented' });
      }

      if (!login || !password) {
        return resolve({ success: false, error: 'missing_credentials' });
      }

      const off = on<EnrolledResult>('biometricEnrolled', (result) => {
        off();
        resolve(result);
      });

      dispatch(new BiometricEnrollEvent({ login, password }));

      setTimeout(() => {
        off();
        resolve({ success: false, error: 'timeout' });
      }, 60_000);
    });
  }

  function unlock(): Promise<UnlockedResult> {
    return new Promise((resolve) => {
      if (!isNativeApp()) {
        return resolve({ success: false, error: 'not_implemented' });
      }

      const off = on<UnlockedResult>('biometricUnlocked', (result) => {
        off();
        resolve(result);
      });

      dispatch(new BiometricUnlockEvent());

      setTimeout(() => {
        off();
        resolve({ success: false, error: 'timeout' });
      }, 60_000);
    });
  }

  /**
   * Apaga a credencial inteira do SecureStore. Usar só em "remover
   * biometria" explícito. Para logout, usar `markAsLocked` em vez disso.
   */
  function disable(): Promise<{ success: boolean }> {
    return new Promise((resolve) => {
      if (!isNativeApp()) {
        return resolve({ success: false });
      }

      const off = on<{ success: boolean }>('biometricDisabled', (result) => {
        off();
        resolve(result);
      });

      dispatch(new BiometricDisableEvent());

      setTimeout(() => {
        off();
        resolve({ success: false });
      }, 5000);
    });
  }

  /**
   * Marca a credencial como bloqueada — não apaga, mas impede o uso até
   * que `unmarkAsLocked` seja chamado após um login manual com sucesso.
   * Disparado em logout iniciado pelo usuário.
   */
  function markAsLocked(): void {
    if (!isNativeApp()) return;
    try {
      localStorage.setItem('biometric_locked', '1');
    } catch {}
  }

  function unmarkAsLocked(): void {
    if (!isNativeApp()) return;
    try {
      localStorage.removeItem('biometric_locked');
    } catch {}
  }

  function isLockedFlagSet(): boolean {
    if (!isNativeApp()) return false;
    try {
      return localStorage.getItem('biometric_locked') === '1';
    } catch {
      return false;
    }
  }

  function markOptInDismissed(): void {
    if (!isNativeApp()) return;
    try {
      localStorage.setItem(OPT_IN_DISMISSED_KEY, '1');
    } catch {}
  }

  function wasOptInDismissed(): boolean {
    if (!isNativeApp()) return false;
    try {
      return localStorage.getItem(OPT_IN_DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  }

  function clearOptInDismissed(): void {
    if (!isNativeApp()) return;
    try {
      localStorage.removeItem(OPT_IN_DISMISSED_KEY);
    } catch {}
  }

  function markCredentialOwner(userId: string): void {
    if (!isNativeApp()) return;
    try {
      localStorage.setItem(CREDENTIAL_OWNER_KEY, userId);
    } catch {}
  }

  function getCredentialOwner(): string | null {
    if (!isNativeApp()) return null;
    try {
      return localStorage.getItem(CREDENTIAL_OWNER_KEY);
    } catch {
      return null;
    }
  }

  function clearCredentialOwner(): void {
    if (!isNativeApp()) return;
    try {
      localStorage.removeItem(CREDENTIAL_OWNER_KEY);
    } catch {}
  }

  /**
   * Flag "pausada pelo usuario". Diferente de locked, eh manipulada
   * pelo toggle em /user/security. A credencial permanece no
   * SecureStore — o app apenas para de oferecer a biometria nos
   * pontos de entrada (gate e botao do Login.vue) ate o usuario
   * reativar pelo toggle confirmando com biometria.
   */
  function markAsDisabled(): void {
    if (!isNativeApp()) return;
    try {
      localStorage.setItem('biometric_disabled', '1');
    } catch {}
  }

  function unmarkAsDisabled(): void {
    if (!isNativeApp()) return;
    try {
      localStorage.removeItem('biometric_disabled');
    } catch {}
  }

  function isDisabledFlagSet(): boolean {
    if (!isNativeApp()) return false;
    try {
      return localStorage.getItem('biometric_disabled') === '1';
    } catch {
      return false;
    }
  }

  return {
    isAvailableEnvironment: isNativeApp,
    checkAvailability,
    enroll,
    unlock,
    disable,
    markAsLocked,
    unmarkAsLocked,
    isLockedFlagSet,
    markAsDisabled,
    unmarkAsDisabled,
    isDisabledFlagSet,
    markOptInDismissed,
    wasOptInDismissed,
    clearOptInDismissed,
    markCredentialOwner,
    getCredentialOwner,
    clearCredentialOwner,
  };
};
